/**
 * Bundle requests and renewals in the Requests inbox (tracker item 85): the enrollments waiting for approval
 * (GET /api/enrollments/pending), each clearly labelled "Bundle request" (a customer asked for a program in the
 * portal) or "Bundle renewal" (a renewal staff or the auto-renew option created), linking to /enrollments/:id where
 * the full Approve / Decline screen is. "Approve here" opens the same approve panel inline. The service request list
 * below it is unchanged.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IBundlesApi } from '../../api';
import * as types from '../../api/types';
import { RequestPager } from '../../components/RequestPager';
import { Toast } from '../../components/Toast';
import { ApproveEnrollmentPanel } from '../../features/service-bundles/ApproveEnrollmentPanel';
import { getErrorMessage } from '../../utils/apiError';
import { formatJobMoney } from '../../utils/jobPresentation';
import { BUNDLE_ENTRY_LABELS, bundleEntryClass, bundleEntrySummary, whenLabel } from './requestsUi';
import { INBOX_PAGE_SIZE, useInboxList } from './useInboxList';

export function BundleIntakeSection() {
  const api = useMemo<IBundlesApi>(() => createAPIClient(), []);
  const [page, setPage] = useState(1);
  const fetchPage = useCallback((pageNo: number) => api.listPendingEnrollments({ page: pageNo, limit: INBOX_PAGE_SIZE }), [api]);
  const { rows, pagination, loading, error, reload } = useInboxList<types.PendingEnrollmentEntry>(fetchPage, page, 'Could not load bundle requests');
  const [approving, setApproving] = useState<{ id: string; enrollment: types.BundleEnrollment | null; error: string | null } | null>(null);
  const [notice, setNotice] = useState<{ id: string; text: string } | null>(null);

  const openApprove = async (entry: types.PendingEnrollmentEntry) => {
    setNotice(null);
    setApproving({ id: entry.id, enrollment: null, error: null });
    try {
      const enrollment = await api.getEnrollment(entry.id);
      setApproving((current) => (current?.id === entry.id ? { id: entry.id, enrollment, error: null } : current));
    } catch (cause) {
      setApproving((current) => (current?.id === entry.id ? { id: entry.id, enrollment: null, error: getErrorMessage(cause, 'Could not open the enrollment') } : current));
    }
  };

  const approved = (enrollment: types.BundleEnrollment) => {
    setApproving(null);
    setNotice({ id: enrollment.id, text: `${enrollment.bundleName} was approved: ${enrollment.stepCount} visit${enrollment.stepCount === 1 ? '' : 's'} scheduled.` });
    void reload();
  };

  // Nothing waiting and nothing wrong: the section does not take any room
  if (!loading && !error && rows.length === 0 && !notice) return null;

  return (
    <section aria-labelledby="bundle-intake-heading" className="mb-6" data-testid="bundle-intake">
      <h2 id="bundle-intake-heading" className="text-lg font-semibold text-gray-900">Bundle requests and renewals</h2>
      <p className="mt-1 text-sm text-gray-600">Service programs waiting for your approval. Approving schedules every visit; declining schedules nothing.</p>

      {notice && (
        <div>
          <Toast kind="success">{notice.text}</Toast>
          <Link to={`/enrollments/${encodeURIComponent(notice.id)}`} className="mb-2 inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">Open the enrollment</Link>
        </div>
      )}
      {error && (
        <div>
          <Toast kind="error">{error}</Toast>
          <button type="button" className="mb-3 min-h-[44px] text-sm font-semibold text-blue-700 hover:underline" onClick={() => void reload()}>Try again</button>
        </div>
      )}
      {loading && rows.length === 0 && !error && <p role="status" className="mt-2 text-gray-600">Loading bundle requests…</p>}

      {rows.length > 0 && (
        <div className="mt-3 overflow-hidden rounded-lg bg-white shadow">
          <table className="responsive-table min-w-full text-sm">
            <caption className="sr-only">Bundle requests and renewals</caption>
            <thead>
              <tr className="border-b text-left text-gray-600">
                <th scope="col" className="px-4 py-3 font-medium">Type</th>
                <th scope="col" className="px-4 py-3 font-medium">Customer</th>
                <th scope="col" className="px-4 py-3 font-medium">Program</th>
                <th scope="col" className="px-4 py-3 font-medium">Where</th>
                <th scope="col" className="px-4 py-3 font-medium">Total</th>
                <th scope="col" className="px-4 py-3 font-medium">Received</th>
                <th scope="col" className="px-4 py-3 font-medium"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((entry) => (
                <React.Fragment key={entry.id}>
                  <tr data-testid="bundle-entry">
                    <td data-label="Type" data-primary className="px-4 py-3">
                      <span className={`rounded px-2 py-0.5 text-xs font-semibold ${bundleEntryClass(entry.type)}`} data-testid="bundle-entry-type">{BUNDLE_ENTRY_LABELS[entry.type]}</span>
                    </td>
                    <td data-label="Customer" className="px-4 py-3 break-words">{entry.customer.name}</td>
                    <td data-label="Program" className="px-4 py-3 break-words">
                      {bundleEntrySummary(entry)}
                      {entry.autoRenew && <div className="text-xs text-gray-500">Auto-renew on</div>}
                    </td>
                    <td data-label="Where" className="px-4 py-3 break-words">{[entry.property.address, entry.property.city].filter(Boolean).join(', ')}</td>
                    <td data-label="Total" className="px-4 py-3">{formatJobMoney(entry.total)}</td>
                    <td data-label="Received" className="px-4 py-3">{whenLabel(entry.createdAt)}</td>
                    <td className="px-4 py-3 md:text-right">
                      <div className="flex flex-wrap gap-x-4 md:justify-end">
                        <Link
                          to={entry.link} className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline"
                          aria-label={`Review ${BUNDLE_ENTRY_LABELS[entry.type].toLowerCase()} from ${entry.customer.name}`}
                        >
                          Review
                        </Link>
                        <button
                          type="button" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline"
                          aria-label={`Approve here: ${entry.bundleName} for ${entry.customer.name}`} onClick={() => void openApprove(entry)}
                        >
                          Approve here
                        </button>
                      </div>
                    </td>
                  </tr>
                  {approving?.id === entry.id && (
                    <tr data-testid="bundle-entry-approve">
                      <td colSpan={7} className="px-4 py-3">
                        {approving.error && <Toast kind="error">{approving.error}</Toast>}
                        {!approving.enrollment && !approving.error && <p role="status" className="text-gray-600">Loading…</p>}
                        {approving.enrollment && <ApproveEnrollmentPanel enrollment={approving.enrollment} onApproved={approved} onCancel={() => setApproving(null)} />}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pagination && <RequestPager page={pagination.page} pages={pagination.pages} total={pagination.total} onPage={setPage} disabled={loading} />}
    </section>
  );
}
